/**
 * Horizontal filter bar for PLP.
 *
 * Displays a compact horizontal bar with:
 * - Filters button (opens modal)
 * - Active filters as removable pills
 * - Results count
 *
 * Supports two positioning modes:
 * - 'sticky': Fixed below header, always visible (default)
 * - 'inline': Scrolls with page, placed inside product grid
 *
 * Multi-brand support:
 * - Gallet: Green border button with funnel icon
 * - Breitling: Plain text button with sliders icon, count on left
 */

'use client';

import { Button } from '@/components/ui/Button';
import type { FacetResults, FilterState, SortOption } from '@/lib/algolia';
import { getBaseFacetName } from '@/lib/algolia/facets';
import { getLabelForId } from '@/lib/algolia/facetValues';
import { useSearchParams } from '@/lib/algolia/hooks/useSearchParams';
import { BRAND } from '@/lib/cms/config';
import { devLog } from '@/lib/utils/devLog';
import { useTranslations } from 'next-intl';
import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { FilterBar } from '../FilterBar';
import styles from './ListingFilters.module.scss';

/** Positioning mode for the filter bar */
export type FilterBarPosition = 'sticky' | 'inline';

/**
 * Sliders / settings-style icon used by both brands.
 */
function SlidersIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      <path
        stroke="currentColor"
        strokeWidth="1.5"
        d="M10.166 8V4M20.4 6h-10M8 6H4M10.166 20v-4M20.4 18h-10M8 18H4M14.234 14v-4M4 12h10M16.4 12h4"
      />
    </svg>
  );
}

/**
 * Filter icon component.
 * Both brands use the sliders/dashboard-style icon. The funnel variant is
 * retained below in case a brand-specific variant is needed in the future.
 */
function FilterIcon({ className }: { className?: string }) {
  return <SlidersIcon className={className} />;
}

interface ListingFiltersProps {
  facets: FacetResults;
  activeFilters: FilterState;
  totalResults: number;
  /**
   * Position mode:
   * - 'sticky': Fixed below header (default)
   * - 'inline': Scrolls with page content
   */
  position?: FilterBarPosition;
  /** Optional: Collection data from CMS with images */
  collectionData?: Array<{
    name: string;
    image?: string;
  }>;
  /** Optional: All available facets (shows full options even when filtered) */
  allFacets?: FacetResults;
  /** Current sort option */
  currentSort?: SortOption;
  /** Product label for results count (e.g., "watches", "straps", "sunglasses") */
  productLabel?: string;
  /** Hide filter controls without reserving filter bar layout space */
  hideControls?: boolean;
}

/**
 * All supported filter parameter names for active filter chips.
 */
type FilterChipParamKey =
  // Watch filters
  | 'collection'
  | 'size'
  | 'case'
  | 'dial-color'
  | 'type'
  | 'bracelet-material'
  | 'bracelet-color'
  // Strap filters
  | 'material'
  | 'color'
  | 'lug'
  | 'lug-type'
  | 'buckle'
  // Eyewear filters
  | 'frame-material'
  | 'lens-color'
  // Store filters
  | 'store-type'
  // Common
  | 'price';

interface FilterChip {
  key: string;
  label: string;
  value: string;
  displayValue: string;
  paramKey: FilterChipParamKey;
}

export function ListingFilters({
  facets,
  activeFilters,
  totalResults,
  position = 'sticky',
  collectionData,
  allFacets,
  currentSort = 'relevance',
  productLabel = 'watches',
  hideControls = false,
}: ListingFiltersProps) {
  const t = useTranslations('plp');

  // Get the appropriate results count translation key based on product type
  const resultsCountKey = `${productLabel}ResultsCount` as
    | 'watchesResultsCount'
    | 'strapsResultsCount'
    | 'sunglassesResultsCount'
    | 'storesResultsCount';
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isScrolledSticky, setIsScrolledSticky] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const { toggleFilterValue, setParams, clearFilters } = useSearchParams();

  // Debug logging
  devLog('[ListingFilters] activeFilters:', activeFilters);
  devLog('[ListingFilters] facets keys:', Object.keys(facets));

  const isSticky = position === 'sticky';

  // Track scroll state for shadow effect (only when sticky)
  useEffect(() => {
    if (!isSticky) return;

    const handleScroll = () => {
      if (wrapperRef.current) {
        const rect = wrapperRef.current.getBoundingClientRect();
        // Account for header height (approx 80px)
        setIsScrolledSticky(rect.top <= 80);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [isSticky]);

  // Helper to get localized label from facets
  const getLabel = (id: string, facetBaseName: string): string => {
    const facetKey = Object.keys(facets).find((key) => getBaseFacetName(key) === facetBaseName);
    if (facetKey && facets[facetKey]) {
      return getLabelForId(id, facets[facetKey], facetBaseName);
    }
    return id;
  };

  // Build list of active filter chips
  const chips: FilterChip[] = [];

  activeFilters.collection?.forEach((id) => {
    chips.push({
      key: `collection-${id}`,
      label: t('facets.collection'),
      value: id,
      displayValue: getLabel(id, 'collection'),
      paramKey: 'collection',
    });
  });

  activeFilters.size?.forEach((id) => {
    chips.push({
      key: `size-${id}`,
      label: t('facets.size'),
      value: id,
      displayValue: `${id}mm`,
      paramKey: 'size',
    });
  });

  activeFilters.case?.forEach((id) => {
    chips.push({
      key: `case-${id}`,
      label: t('facets.case'),
      value: id,
      displayValue: getLabel(id, 'case'),
      paramKey: 'case',
    });
  });

  activeFilters.dialColor?.forEach((id) => {
    chips.push({
      key: `dial-color-${id}`,
      label: t('facets.dialColor'),
      value: id,
      displayValue: getLabel(id, 'dial_color'),
      paramKey: 'dial-color',
    });
  });

  activeFilters.type?.forEach((id) => {
    chips.push({
      key: `type-${id}`,
      label: t('facets.type'),
      value: id,
      displayValue: getLabel(id, 'type'),
      paramKey: 'type',
    });
  });

  activeFilters.braceletMaterial?.forEach((id) => {
    chips.push({
      key: `bracelet-material-${id}`,
      label: t('facets.braceletMaterial'),
      value: id,
      displayValue: getLabel(id, 'bracelet_category_material'),
      paramKey: 'bracelet-material',
    });
  });

  activeFilters.braceletColor?.forEach((id) => {
    chips.push({
      key: `bracelet-color-${id}`,
      label: t('facets.braceletColor'),
      value: id,
      displayValue: getLabel(id, 'bracelet_category_color'),
      paramKey: 'bracelet-color',
    });
  });

  // ============================================
  // Strap filter chips
  // ============================================

  activeFilters.material?.forEach((id) => {
    chips.push({
      key: `material-${id}`,
      label: t('facets.material'),
      value: id,
      displayValue: getLabel(id, 'material'),
      paramKey: 'material',
    });
  });

  activeFilters.color?.forEach((id) => {
    chips.push({
      key: `color-${id}`,
      label: t('facets.color'),
      value: id,
      displayValue: getLabel(id, 'color'),
      paramKey: 'color',
    });
  });

  activeFilters.lug?.forEach((id) => {
    chips.push({
      key: `lug-${id}`,
      label: t('facets.lugWidth'),
      value: id,
      displayValue: `${id}mm`,
      paramKey: 'lug',
    });
  });

  activeFilters.lugType?.forEach((id) => {
    chips.push({
      key: `lug-type-${id}`,
      label: t('facets.lugType'),
      value: id,
      displayValue: getLabel(id, 'lug_type'),
      paramKey: 'lug-type',
    });
  });

  activeFilters.buckle?.forEach((id) => {
    chips.push({
      key: `buckle-${id}`,
      label: t('facets.buckle'),
      value: id,
      displayValue: getLabel(id, 'buckle'),
      paramKey: 'buckle',
    });
  });

  // ============================================
  // Eyewear filter chips
  // ============================================

  activeFilters.frameMaterial?.forEach((id) => {
    chips.push({
      key: `frame-material-${id}`,
      label: t('facets.frameMaterial'),
      value: id,
      displayValue: getLabel(id, 'frame_material'),
      paramKey: 'frame-material',
    });
  });

  activeFilters.lensColor?.forEach((id) => {
    chips.push({
      key: `lens-color-${id}`,
      label: t('facets.lensColor'),
      value: id,
      displayValue: getLabel(id, 'lens_color'),
      paramKey: 'lens-color',
    });
  });

  // Store type chips
  const storeTypeFilters = (activeFilters as Record<string, string[] | undefined>).storeType;
  if (Array.isArray(storeTypeFilters)) {
    // Format store type display value (e.g., "FLAG_SHIP_BOUTIQUE" -> "Flagship Boutique")
    const formatStoreType = (type: string) => {
      return type
        .split('_')
        .map((word) => word.charAt(0) + word.slice(1).toLowerCase())
        .join(' ');
    };
    storeTypeFilters.forEach((id: string) => {
      chips.push({
        key: `store-type-${id}`,
        label: t('facets.storeType'),
        value: id,
        displayValue: formatStoreType(id),
        paramKey: 'store-type',
      });
    });
  }

  if (activeFilters.priceMin !== undefined || activeFilters.priceMax !== undefined) {
    const min = activeFilters.priceMin ?? 0;
    const max = activeFilters.priceMax;
    const priceLabel = max
      ? `$${min.toLocaleString()} – $${max.toLocaleString()}`
      : `$${min.toLocaleString()}+`;
    chips.push({
      key: 'price',
      label: t('facets.price'),
      value: 'price',
      displayValue: priceLabel,
      paramKey: 'price',
    });
  }

  const handleRemoveChip = (chip: FilterChip) => {
    if (chip.paramKey === 'price') {
      setParams({ 'price-min': null, 'price-max': null, page: 1 });
    } else {
      toggleFilterValue(chip.paramKey, chip.value);
    }
  };

  const activeFilterCount = chips.length;

  return (
    <>
      <div
        ref={wrapperRef}
        className={`${styles.wrapper} ${isSticky ? styles.stickyMode : styles.inlineMode} ${isScrolledSticky ? styles.scrolled : ''} ${hideControls ? styles.hiddenControls : ''}`}
        data-testid="listing-filters"
        aria-hidden={hideControls || undefined}
      >
        <div className={styles.container}>
          {/* Filters button */}
          <button
            type="button"
            className={styles.filtersButton}
            onClick={() => setIsModalOpen(true)}
            aria-label={t('openFilters')}
          >
            <span className={styles.filtersButtonText}>{t('filters')}</span>
            {activeFilterCount > 0 && (
              <span className={styles.filterCount}>{activeFilterCount}</span>
            )}
            <FilterIcon className={styles.filterIcon} />
          </button>

          {/* Active filters (desktop only) */}
          {chips.length > 0 && (
            <div className={styles.activeFilters}>
              <div className={styles.chips}>
                {chips.map((chip) => (
                  <button
                    key={`desktop-${chip.key}`}
                    type="button"
                    onClick={() => handleRemoveChip(chip)}
                    className={styles.chip}
                    aria-label={`${t('facets.remove')} ${chip.label}: ${chip.displayValue}`}
                  >
                    <span className={styles.chipValue}>{chip.displayValue}</span>
                    <svg
                      className={styles.removeIcon}
                      width="12"
                      height="12"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                    >
                      <line x1="18" y1="6" x2="6" y2="18" />
                      <line x1="6" y1="6" x2="18" y2="18" />
                    </svg>
                  </button>
                ))}
              </div>

              <button type="button" onClick={clearFilters} className={styles.clearAll}>
                {t('facets.clear')}
              </button>
            </div>
          )}

          {/* Clear all button (mobile only - in top row) */}
          {chips.length > 0 && (
            <button type="button" onClick={clearFilters} className={styles.clearAllMobile}>
              {t('facets.clear')}
            </button>
          )}

          {/* Results count */}
          <div className={styles.resultsCount}>
            <span>{t(resultsCountKey, { count: totalResults })}</span>
          </div>
        </div>

        {/* Mobile chips row - scrollable single row below top bar */}
        {chips.length > 0 && (
          <div className={styles.mobileChipsRow}>
            {chips.map((chip) => (
              <button
                key={`mobile-${chip.key}`}
                type="button"
                onClick={() => handleRemoveChip(chip)}
                className={styles.chip}
                aria-label={`${t('facets.remove')} ${chip.label}: ${chip.displayValue}`}
              >
                <span className={styles.chipValue}>{chip.displayValue}</span>
                <svg
                  className={styles.removeIcon}
                  width="12"
                  height="12"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Filter Modal — portalled to <body> so it escapes ancestor stacking
          contexts (e.g. the PLP section's clip-path used for full-bleed bg) */}
      {isModalOpen &&
        !hideControls &&
        typeof document !== 'undefined' &&
        createPortal(
          <div className={styles.modalOverlay} onClick={() => setIsModalOpen(false)}>
            <div
              className={styles.modal}
              onClick={(e) => e.stopPropagation()}
              role="dialog"
              aria-modal="true"
              aria-label={t('filters')}
            >
              <div className={styles.modalHeader}>
                <h2 className={styles.modalTitle}>{t('filters')}</h2>
                <button
                  type="button"
                  className={styles.closeButton}
                  onClick={() => setIsModalOpen(false)}
                  aria-label={t('facets.close')}
                >
                  <svg
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    <line x1="18" y1="6" x2="6" y2="18" />
                    <line x1="6" y1="6" x2="18" y2="18" />
                  </svg>
                </button>
              </div>
              <div className={styles.modalContent}>
                {/* FilterBar displays filters inside modal - suppress its header and border */}
                <FilterBar
                  facets={facets}
                  activeFilters={activeFilters}
                  hideHeader
                  embedded
                  collectionData={collectionData}
                  allFacets={allFacets}
                  currentSort={currentSort}
                  productLabel={productLabel}
                />
              </div>
              <div className={styles.modalFooter}>
                {/* Reset (outline) + Apply. Gallet's Apply uses the green
                    secondary variant; Breitling keeps its primary treatment. */}
                <Button
                  variant="outline"
                  fullWidth
                  className={styles.resetButton}
                  onClick={() => {
                    clearFilters();
                  }}
                >
                  {t('resetFilters')}
                </Button>
                <Button
                  variant={BRAND === 'gallet' ? 'secondary' : 'primary'}
                  fullWidth
                  className={styles.applyButton}
                  onClick={() => setIsModalOpen(false)}
                >
                  {t('facets.apply')}
                </Button>
              </div>
            </div>
          </div>,
          document.body
        )}
    </>
  );
}
